import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Video, Users, Clock, Calendar as CalendarIcon, Send } from "lucide-react";

export const EnhancedMeetingScheduler = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    description: "",
    meeting_date: new Date(),
    duration_minutes: 60,
    meeting_link: "",
    meeting_type: "team" as "team" | "client" | "internal",
    priority: "medium" as "low" | "medium" | "high",
    agenda: "",
  });
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringPattern, setRecurringPattern] = useState<"daily" | "weekly" | "monthly">("weekly");

  const teamMembersQuery = useQuery({
    queryKey: ["team-members-for-meeting"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: userRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id")
        .in("role", ["super_admin", "admin", "member"]);

      if (rolesError) {
        console.error("Error fetching user roles:", rolesError);
        return [];
      }

      if (!userRoles || userRoles.length === 0) return [];

      const userIds = userRoles.map((r: any) => r.user_id);
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .in("id", userIds)
        .neq("id", user.id);

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        return [];
      }
      
      return profiles || [];
    },
  });

  const scheduleMeetingMutation = useMutation({
    mutationFn: async (meetingData: typeof newMeeting) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: meeting, error: meetingError } = await supabase
        .from("team_meetings")
        .insert([
          {
            title: meetingData.title,
            description: meetingData.description,
            meeting_date: meetingData.meeting_date.toISOString(),
            duration_minutes: meetingData.duration_minutes,
            meeting_link: meetingData.meeting_link,
            user_id: user.id,
            organizer_id: user.id,
          },
        ])
        .select()
        .single();

      if (meetingError) throw meetingError;

      if (selectedAttendees.length > 0 && meeting) {
        const attendeeRecords = selectedAttendees.map(attendeeId => ({
          meeting_id: meeting.id,
          user_id: attendeeId,
          status: 'pending',
        }));

        const { error: attendeeError } = await supabase
          .from("team_meeting_attendees")
          .insert(attendeeRecords);

        if (attendeeError) throw attendeeError;
      }

      if (sendEmail && selectedAttendees.length > 0) {
        const attendeeEmails = teamMembersQuery.data
          ?.filter((member: any) => selectedAttendees.includes(member.id))
          .map((member: any) => member.email)
          .filter(Boolean);

        if (attendeeEmails && attendeeEmails.length > 0) {
          await supabase.functions.invoke("send-notifications", {
            body: {
              to: attendeeEmails,
              subject: `New Team Meeting: ${meetingData.title}`,
              html: `
                <h2>You've been invited to a team meeting</h2>
                <p><strong>Title:</strong> ${meetingData.title}</p>
                <p><strong>Date:</strong> ${new Date(meetingData.meeting_date).toLocaleString()}</p>
                <p><strong>Duration:</strong> ${meetingData.duration_minutes} minutes</p>
                ${meetingData.description ? `<p><strong>Description:</strong> ${meetingData.description}</p>` : ''}
                ${meetingData.meeting_link ? `<p><strong>Join Link:</strong> <a href="${meetingData.meeting_link}">${meetingData.meeting_link}</a></p>` : ''}
              `,
              type: 'email',
            },
          });
        }
      }

      return meeting;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Meeting scheduled successfully and invitations sent!",
      });
      setOpen(false);
      setNewMeeting({
        title: "",
        description: "",
        meeting_date: new Date(),
        duration_minutes: 60,
        meeting_link: "",
        meeting_type: "team",
        priority: "medium",
        agenda: "",
      });
      setSelectedAttendees([]);
      setSendEmail(true);
      setIsRecurring(false);
      queryClient.invalidateQueries({ queryKey: ["team-meetings"] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to schedule meeting",
        variant: "destructive",
      });
    },
  });

  const handleScheduleMeeting = () => {
    if (selectedAttendees.length === 0) {
      toast({
        title: "No attendees selected",
        description: "Please select at least one team member to invite",
        variant: "destructive",
      });
      return;
    }
    scheduleMeetingMutation.mutate(newMeeting);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Schedule Meeting
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Schedule New Meeting
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Meeting Info Card */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Meeting Title</Label>
                <Input
                  id="title"
                  value={newMeeting.title}
                  onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                  placeholder="Enter meeting title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Meeting Type</Label>
                  <Select 
                    value={newMeeting.meeting_type} 
                    onValueChange={(value: any) => setNewMeeting({ ...newMeeting, meeting_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team">Team Meeting</SelectItem>
                      <SelectItem value="client">Client Meeting</SelectItem>
                      <SelectItem value="internal">Internal Review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={newMeeting.priority} 
                    onValueChange={(value: any) => setNewMeeting({ ...newMeeting, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low Priority</SelectItem>
                      <SelectItem value="medium">Medium Priority</SelectItem>
                      <SelectItem value="high">High Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newMeeting.description}
                  onChange={(e) => setNewMeeting({ ...newMeeting, description: e.target.value })}
                  placeholder="Enter meeting description"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="agenda">Agenda</Label>
                <Textarea
                  id="agenda"
                  value={newMeeting.agenda}
                  onChange={(e) => setNewMeeting({ ...newMeeting, agenda: e.target.value })}
                  placeholder="Meeting agenda items (optional)"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Schedule Card */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <CalendarIcon className="h-4 w-4" />
                <Label className="text-base font-semibold">Schedule</Label>
              </div>

              <div className="space-y-2">
                <Label>Meeting Date & Time</Label>
                <Calendar
                  mode="single"
                  selected={newMeeting.meeting_date}
                  onSelect={(date) => date && setNewMeeting({ ...newMeeting, meeting_date: date })}
                  className="rounded-md border"
                />
                <Input
                  type="time"
                  value={newMeeting.meeting_date.toTimeString().slice(0, 5)}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(":");
                    const newDate = new Date(newMeeting.meeting_date);
                    newDate.setHours(parseInt(hours), parseInt(minutes));
                    setNewMeeting({ ...newMeeting, meeting_date: newDate });
                  }}
                  className="mt-2"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Select 
                  value={newMeeting.duration_minutes.toString()} 
                  onValueChange={(value) => setNewMeeting({ ...newMeeting, duration_minutes: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1.5 hours</SelectItem>
                    <SelectItem value="120">2 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="recurring"
                  checked={isRecurring}
                  onCheckedChange={(checked) => setIsRecurring(checked as boolean)}
                />
                <Label htmlFor="recurring" className="text-sm cursor-pointer">
                  Recurring meeting
                </Label>
              </div>

              {isRecurring && (
                <div className="space-y-2 pl-6">
                  <Label>Repeat Pattern</Label>
                  <Select 
                    value={recurringPattern} 
                    onValueChange={(value: any) => setRecurringPattern(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Meeting Link Card */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Video className="h-4 w-4" />
                <Label className="text-base font-semibold">Virtual Meeting</Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="meeting_link">Meeting Link (Optional)</Label>
                <Input
                  id="meeting_link"
                  value={newMeeting.meeting_link}
                  onChange={(e) => setNewMeeting({ ...newMeeting, meeting_link: e.target.value })}
                  placeholder="Enter Zoom, Meet, or Teams link"
                />
              </div>
            </CardContent>
          </Card>

          {/* Attendees Card */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" />
                <Label className="text-base font-semibold">Attendees ({selectedAttendees.length} selected)</Label>
              </div>
              {teamMembersQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading team members...</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {teamMembersQuery.data?.map((member: any) => (
                    <div key={member.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={member.id}
                        checked={selectedAttendees.includes(member.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAttendees([...selectedAttendees, member.id]);
                          } else {
                            setSelectedAttendees(
                              selectedAttendees.filter((id) => id !== member.id)
                            );
                          }
                        }}
                      />
                      <label
                        htmlFor={member.id}
                        className="text-sm font-medium leading-none cursor-pointer"
                      >
                        {member.first_name} {member.last_name}
                        {member.email && (
                          <span className="text-muted-foreground ml-2">
                            ({member.email})
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center space-x-2 pt-4 border-t">
                <Checkbox
                  id="send-email"
                  checked={sendEmail}
                  onCheckedChange={(checked) => setSendEmail(checked as boolean)}
                />
                <Label htmlFor="send-email" className="text-sm cursor-pointer flex items-center gap-2">
                  <Send className="h-3 w-3" />
                  Send email invitations to attendees
                </Label>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleScheduleMeeting}
              disabled={scheduleMeetingMutation.isPending || !newMeeting.title || selectedAttendees.length === 0}
              className="flex-1"
            >
              {scheduleMeetingMutation.isPending ? (
                <>
                  <Clock className="mr-2 h-4 w-4 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Schedule Meeting
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};